import { createSelector } from 'reselect';

import { CameraStatus } from 'modules/cameras/types';
import { filterRequestFormatter } from 'modules/interventions/utils';
import type { RootState } from 'reducers/index';
import { getSelectedDealerId } from 'selectors/dealerSelectors';
import { getProps, getRoot } from 'utils/selectors';

const getState = (state: RootState) => state.cameras;

const getDataState = createSelector(getState, state => state.data);

const getUIState = createSelector(getState, state => state.ui);

const getCameraEntries = createSelector(getDataState, data => data.entities);

export const getCameraList = createSelector(getCameraEntries, entries => Object.values(entries));

export const getCameraById = createSelector(getCameraEntries, getProps<string>, (entries, id) => entries[id]);

const getCamerasByIds = createSelector(
  getCameraEntries,
  getProps<Array<string>>,
  (cameras, camerasIds) => Object.values(cameras).filter(camera => camerasIds.includes(camera.id))
);

const getUIDashboard = createSelector(getUIState, ui => ui.dashboard);

const getUIDashboardData = createSelector(getUIDashboard, dashboard => dashboard.data);

const getDashboardIds = createSelector(getUIDashboardData, data => data.ids);

export const isDashboardLoading = createSelector(getUIDashboardData, data => data.loading);

export const getDashboardEntries = createSelector(getRoot, getDashboardIds, getCamerasByIds);

export const getFilters = createSelector(getUIDashboard, dashboard => dashboard.filters);

const getUIDetails = createSelector(getUIState, ui => ui.details);

const getLoadingDetailsState = createSelector(getUIDetails, ui => ui.loading);

const creating = createSelector(getLoadingDetailsState, loading => loading.create);

const updating = createSelector(getLoadingDetailsState, loading => loading.update);

export const fetching = createSelector(getLoadingDetailsState, loading => loading.fetch);

export const getScansByCameraId = createSelector(getCameraById, camera => camera?.scans);

export const fetchingScans = createSelector(getLoadingDetailsState, loading => loading.scans);

export const isPairingTv = createSelector(getLoadingDetailsState, loading => loading.pairing);

const getHardware = createSelector(getState, state => state.hardware);

const getHardwareData = createSelector(getHardware, hardware => hardware.data);

export const getHardwareEntries = createSelector(getHardwareData, data => data.entities);

const getHardwareUI = createSelector(getHardware, hardware => hardware.ui);

export const isFetchingHardware = createSelector(getHardwareUI, ui => ui.loading);

export const ready = createSelector(getUIDetails, ui => ui.ready);

export const saving = createSelector(creating, updating, (create, update) => create || update);

export const getBaseRequestParams = createSelector(
  getSelectedDealerId,
  getFilters,
  (dealerId, filters) => ({
    status: [CameraStatus.ACTIVE, CameraStatus.INACTIVE],
    dealerId,
    ...filterRequestFormatter(filters),
  })
);

export const deletingDashboardItem = createSelector(
  getUIDashboard,
  getProps<string>,
  (dashboard, cameraId) => dashboard.deleting.includes(cameraId)
);

export const patchingDashboardItem = createSelector(
  getUIDashboard,
  getProps<string>,
  (dashboard, cameraId) => dashboard.patching.includes(cameraId)
);

export const isModifyingDashboardItem = createSelector(
  deletingDashboardItem,
  patchingDashboardItem,
  (deleting, patching) => deleting || patching
);

export const isPairingModalOpenSelector = createSelector(getUIDetails, ui => ui.isPairingModalOpen);
